import { nameUnit } from '@/validations/validatorUnits'
import { required, maxLength, numeric } from '@vuelidate/validators'
import {
  getHasBlockNumbers,
  getHasFreeSingleNumbers,
  getIsNumberModeBlock,
  getIsValidDirectDial,
  getIsValidQuickDial,
} from './validators'
import router from '@/router'
import type { Config } from './tour'
import { useDevStore } from '@/stores/dev/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useCtaStore } from '@/stores/cta/store'
import { usePromptsStore } from '@/stores/prompts/store'
import { useNotificationStore } from '@/stores/notifications/store'
import { computed } from 'vue'
import { i18n } from '@/core/i18n'

/*
  Requirements:
    - Is not basic edition
    - Spare license
    - At least 1 extension
    - At least 1 IVR audio file
    - At least 1 block number or free single number
  Create IVR extension
    - Select IVR mode
    - Select IVR audio file (use the standard audio as the demo audio)
    - Set timeout (only counts after the audio is finished)
    - Set key events
*/

export const CTAIVRSetupConfig: Config = {
  onStart() {
    // Redirect to overview
    router.push('/').catch((error) => {
      if (error.name !== 'NavigationDuplicated') {
        throw error
      }
    })

    // Force re-render to reset the view (if in edit extensions mode, the add button is hidden)
    useDevStore().forceRerender()
  },
  steps: [
    // Show requirements
    {
      delay: 200, // Initial delay to make sure the onStart has had effect
      title: 'tutorial.cta.ivrSetup.intro.title',
      text: 'tutorial.cta.ivrSetup.intro.text',
      showNextButton: true,
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      canGoToNext() {
        // Check if is not Basic edition

        const edition = useCtaStore().getEdition

        const isBasicEdition = edition === 'basic'

        if (isBasicEdition) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.ivrSetup.intro.requirements.basicEdition.invalid'
              )
            ),
          })

          return false
        }

        // Check if needs to upgrade their plan

        const extensions = useExtensionsStore().list

        const ctaData = useCtaStore().get

        const hasMaxExtensions = extensions.length >= (ctaData.maxLines ?? 0)

        const needsToUpgradePlan = hasMaxExtensions

        if (needsToUpgradePlan) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.ivrSetup.intro.requirements.maxLines.invalid'
              )
            ),
          })

          return false
        }

        // Check has at least 1 extension already set up

        const hasExtensions = extensions.length

        if (!hasExtensions) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.ivrSetup.intro.requirements.hasExtension.invalid'
              )
            ),
          })

          return false
        }

        // Check has at least 1 IVR audio file

        const audioFiles = usePromptsStore().list

        const hasIvrAudioFiles = audioFiles.filter(
          // id 4 is the predefined IVR music from easybell, which can't be used
          (audioFile) =>
            audioFile.type === 'waiting_music' && audioFile.id !== 4
        ).length

        if (!hasIvrAudioFiles) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.ivrSetup.intro.requirements.hasIvrAudioFile.invalid'
              )
            ),
          })

          return false
        }

        // Check if has any usable numbers for the new extension

        const hasBlockNumbers = getHasBlockNumbers()

        const hasFreeSingleNumbers = getHasFreeSingleNumbers()

        if (!hasBlockNumbers && !hasFreeSingleNumbers) {
          useNotificationStore().add({
            type: 'error',
            title: computed(() =>
              i18n.global.t(
                'tutorial.cta.ivrSetup.intro.requirements.freeNumbers.invalid'
              )
            ),
          })

          return false
        }

        return true
      },
    },
    // Create an extension: add extension
    {
      title: 'tutorial.cta.ivrSetup.addExtension.title',
      text: 'tutorial.cta.ivrSetup.addExtension.text',
      attachElement:
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
      nextOnClickElements: [
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
      ],
      attachPosition: 'top',
    },
    // Create an extension: set name
    {
      waitForElements: ['[data-test=extension-page][data-is-loading=false]'],
      title: 'tutorial.cta.ivrSetup.extension.name.title',
      text: 'tutorial.cta.ivrSetup.extension.name.text',
      showNextButton: true,
      attachElement: '[data-test=extension-name]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=extension-name]'],
      preventEnterAndTabInElements: ['[data-test=extension-name]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=extension-name]'
        )

        const value = element?.value

        if (
          !nameUnit.required.$validator(value, undefined, undefined) ||
          !nameUnit.minLength.$validator(value, undefined, undefined) ||
          !nameUnit.maxLength.$validator(value, undefined, undefined)
        ) {
          return false
        }

        // Check if name already used

        const extensions = useExtensionsStore().list

        const extensionNames = extensions.map((extension) => {
          return extension.name
        })

        const isNameAlreadyUsed = extensionNames.some(
          (name) => name.toLowerCase() === value?.toLowerCase()
        )

        if (isNameAlreadyUsed) {
          return false
        }

        return true
      },
    },
    // Create an extension: set extension type
    {
      title: 'tutorial.cta.ivrSetup.extension.type.title',
      text: 'tutorial.cta.ivrSetup.extension.type.text',
      showNextButton: true,
      attachElement: '[data-test=extensionType]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=extensionType]'],
      preventEnterAndTabInElements: ['[data-test=extensionType]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=extensionType]'
        )

        const value = element?.value

        // Check that extension type is set to "IVR"
        return value === 'ivr'
      },
    },
    // Create an extension: set number mode
    {
      title: 'tutorial.cta.ivrSetup.extension.numberMode.title',
      text: 'tutorial.cta.ivrSetup.extension.numberMode.text',
      showNextButton: true,
      attachElement: '[data-test=numberMode]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=numberMode]'],
      preventEnterAndTabInElements: ['[data-test=numberMode]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=numberMode]'
        )

        const value = element?.value

        switch (value) {
          case 'false':
            return getHasBlockNumbers()

          case 'true':
            return getHasFreeSingleNumbers()
        }

        return false
      },
    },
    // Create an extension: set phone number
    {
      title: 'tutorial.cta.ivrSetup.extension.phoneNumber.title',
      text: 'tutorial.cta.ivrSetup.extension.phoneNumber.text',
      showNextButton: true,
      attachElement: '[data-test=phone-number]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=phone-number]'],
      preventEnterAndTabInElements: ['[data-test=phone-number]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=phone-number]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create an extension: set direct dial (only for Block numbers)
    {
      title: 'tutorial.cta.ivrSetup.extension.directDial.title',
      text: 'tutorial.cta.ivrSetup.extension.directDial.text',
      showNextButton: true,
      attachElement: '[data-test=dial-options]',
      attachPosition: 'top',
      showOn: getIsNumberModeBlock,
      watchValueOfElements: [
        '[data-test=direct-dial]',
        '#directDialIsQuickDial',
        '[data-test=quick-dial]',
      ],
      preventEnterAndTabInElements: [
        '[data-test=direct-dial]',
        '#directDialIsQuickDial',
        '[data-test=quick-dial]',
      ],
      canGoToNext() {
        const directDialElement = document.querySelector<HTMLInputElement>(
          '[data-test=direct-dial]'
        )

        const directDial = directDialElement?.value

        const quickDialElement = document.querySelector<HTMLInputElement>(
          '[data-test=quick-dial]'
        )

        const quickDial = quickDialElement?.value

        const trunkSelector = document.querySelector<HTMLSelectElement>(
          '[data-test=phone-number]'
        )

        const trunkId = trunkSelector?.value

        const isDirectDialValid = getIsValidDirectDial(
          directDial ?? '',
          trunkId ?? ''
        )

        const isQuickDialValid = getIsValidQuickDial(quickDial ?? '')

        return isDirectDialValid && isQuickDialValid
      },
    },
    // Create an extension: set quick dial (only for single numbers)
    {
      title: 'tutorial.cta.ivrSetup.extension.quickDial.title',
      text: 'tutorial.cta.ivrSetup.extension.quickDial.text',
      showNextButton: true,
      attachElement: '[data-test=quick-dial]',
      attachPosition: 'top',
      showOn() {
        const isBlockMode = getIsNumberModeBlock()

        return !isBlockMode
      },
      watchValueOfElements: ['[data-test=quick-dial]'],
      preventEnterAndTabInElements: ['[data-test=quick-dial]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=quick-dial]'
        )

        const value = element?.value

        return getIsValidQuickDial(value ?? '')
      },
    },
    // Create an extension: set IVR audio file
    {
      title: 'tutorial.cta.ivrSetup.extension.ivrAudiofile.title',
      text: 'tutorial.cta.ivrSetup.extension.ivrAudiofile.text',
      showNextButton: true,
      attachElement: '[data-test=ivr-audiofile]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=ivr-audiofile]'],
      preventEnterAndTabInElements: ['[data-test=ivr-audiofile]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=ivr-audiofile]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create an extension: set IVR timeout
    {
      title: 'tutorial.cta.ivrSetup.extension.ivrTimeout.title',
      text: 'tutorial.cta.ivrSetup.extension.ivrTimeout.text',
      showNextButton: true,
      attachElement: '[data-test=ivr-timeout]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=ivr-timeout]'],
      preventEnterAndTabInElements: ['[data-test=ivr-timeout]'],
      canGoToNext() {
        const element = document.querySelector<HTMLInputElement>(
          '[data-test=ivr-timeout]'
        )

        const value = element?.value

        return (required.$validator(value, undefined, undefined) &&
          numeric.$validator(value, undefined, undefined) &&
          maxLength(4).$validator(value, undefined, undefined)) as boolean
      },
    },
    // Create an extension: set IVR key 1
    {
      title: 'tutorial.cta.ivrSetup.extension.ivrKey1.title',
      text: 'tutorial.cta.ivrSetup.extension.ivrKey1.text',
      showNextButton: true,
      attachElement: '[data-test=ivr-extension-1]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=ivr-extension-1]'],
      preventEnterAndTabInElements: ['[data-test=ivr-extension-1]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=ivr-extension-1]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create an extension: set IVR default extension
    {
      title: 'tutorial.cta.ivrSetup.extension.ivrDefault.title',
      text: 'tutorial.cta.ivrSetup.extension.ivrDefault.text',
      showNextButton: true,
      attachElement: '[data-test=ivr-default-extension]',
      attachPosition: 'top',
      watchValueOfElements: ['[data-test=ivr-default-extension]'],
      preventEnterAndTabInElements: ['[data-test=ivr-default-extension]'],
      canGoToNext() {
        const element = document.querySelector<HTMLSelectElement>(
          '[data-test=ivr-default-extension]'
        )

        const value = element?.value

        return required.$validator(value, undefined, undefined) as boolean
      },
    },
    // Create an extension: save changes
    {
      title: 'tutorial.cta.ivrSetup.extension.save.title',
      text: 'tutorial.cta.ivrSetup.extension.save.text',
      attachElement: '[data-test=action-btn-save]',
      nextOnClickElements: ['[data-test=action-btn-save]'],
      attachPosition: 'top',
    },
    // Overview: show where extensions are viewed
    {
      title: 'tutorial.cta.ivrSetup.extensionOverview.title',
      text: 'tutorial.cta.ivrSetup.extensionOverview.text',
      showFinishButton: true,
      attachElement: '[data-test=overview-section-extension]',
      waitForElements: [
        '[data-test=overview-section-extension] [data-test=section-container][data-loading=false]',
      ],
      attachPosition: 'top',
      preventClickInElements: [
        '[data-test=overview-section-extension] [data-test=show-multi-edit]',
        '[data-test=overview-section-extension] [data-test=add-new-section-item]',
        '[data-test=edit-extension]',
        '[data-test=overview-section-extension] [data-test=open-filter]',
        '[data-test=edit-call-forwarding]',
        '[data-test=edit-wait-loop]',
        '[data-test=catch-all]',
        '[data-test=edit-night-switch]',
        '[data-test*=add-group-]',
        '[data-test*=group-]',
        '[data-test=extension-item-color]',
      ],
    },
  ],
}
